import type PptxGenJS from '@meitu/js-to-pptx';
import { pxToInch } from '@/utils/unit-kit';
import { ChartType, IChartLayer, type ISlide } from '@/version-types';
import { getBarGapInfo, getFontsByPosition } from './core';

const getPPTChartType = (chartType: ChartType): PptxGenJS.CHART_NAME => {
    if (chartType.includes('bar')) return 'bar';
    if (chartType.includes('doughnut')) return 'doughnut';
    if (chartType.includes('line')) return 'line';
    if (chartType === 'pie-basic') return 'pie';
    if (chartType === 'pie-doughnut') return 'doughnut';
    throw new Error(`Unsupported chart type: ${chartType}`);
};

const getPPTColor = (color: string) => color.replace('#', '').slice(0, 6);

export const getPPTChartInfo = (layer: IChartLayer) => {
    const { type, chartData, colors, legend, grid, series, categoryAxisLabel, valueAxisLabel } = layer.data;

    const col1Texts = chartData
        .slice(1)
        .map((row) => row[0])
        .map((item) => (item === null ? '' : `${item}`));
    const row1Texts = chartData[0].slice(1).map((item) => (item === null ? '' : `${item}`));

    const seriesTexts = row1Texts;
    const labelTexts = col1Texts;

    const pptChartData = seriesTexts.map((name, sIdx) => ({
        name,
        labels: labelTexts,
        values: chartData.slice(1).map((row) => row[sIdx + 1]),
    }));

    const options: PptxGenJS.IChartOpts = {
        x: pxToInch(layer.left),
        y: pxToInch(layer.top),
        w: pxToInch(layer.width),
        h: pxToInch(layer.height),
        chartColors: colors.map(getPPTColor),
        showLegend: legend.show,
        legendPos: (
            {
                top: 't',
                left: 'l',
                bottom: 'b',
                right: 'r',
            } as const
        )[legend.position],
        legendColor: legend.textStyle.color ? getPPTColor(legend.textStyle.color) : undefined,
        legendFontSize: Math.floor(legend.textStyle.fontSize),
        legendFontFace: legend.textStyle.fontFamily,
        showValue: series.label.show,
        dataLabelFontFace: series.label.textStyle.fontFamily,
        dataLabelFontSize: Math.floor(series.label.textStyle.fontSize),
        dataLabelColor: series.label.textStyle.color ? getPPTColor(series.label.textStyle.color) : undefined,
        catAxisLabelFontFace: categoryAxisLabel.textStyle.fontFamily,
        catAxisLabelFontSize: Math.floor(categoryAxisLabel.textStyle.fontSize),
        catAxisLabelColor: categoryAxisLabel.textStyle.color
            ? getPPTColor(categoryAxisLabel.textStyle.color)
            : undefined,
        valAxisLabelFontFace: valueAxisLabel.textStyle.fontFamily,
        valAxisLabelFontSize: Math.floor(valueAxisLabel.textStyle.fontSize),
        valAxisLabelColor: valueAxisLabel.textStyle.color ? getPPTColor(valueAxisLabel.textStyle.color) : undefined,
        catGridLine: { style: grid.show ? 'solid' : 'none' },
        valGridLine: { style: grid.show ? 'solid' : 'none' },
        barGapWidthPct: getBarGapInfo(seriesTexts.length).barGapWidthPct * 100,
        barOverlapPct: -1 * getBarGapInfo(seriesTexts.length).barGap * 100,
        barDir: type.includes('horizontal') ? 'bar' : 'col',
        barGrouping: type.includes('stacked') ? 'stacked' : 'clustered',
    };

    return {
        type: getPPTChartType(type),
        pptChartData,
        options,
    };
};

export const savePPTChart = (
    layer: IChartLayer,
    /** ppt slide */
    slide: ISlide,
    /** 外部传入的字体列表，可变对象，该方法会改变该数组成员 */
    externalFonts?: string[],
) => {
    const { type, pptChartData, options } = getPPTChartInfo(layer);

    slide.addChart(type, pptChartData, options);

    if (externalFonts) {
        externalFonts.push(...getFontsByPosition(layer.data, 'all').map((f) => f.fontFamily));
    }
};
